/**
 * Fleet-list vehicle status + alerts.
 *
 * Ported 1:1 (behaviour) from the legacy mapv3 fleet panel (digitrack/web/v3/js/mapv3-fleet.js)
 * so the digitrack2 map list classifies vehicles identically. Field names are mapped to the v2
 * `last_position` payload (DataLast): speed [km/h], ignition [bool], voltage [mV], satellites,
 * device_timestamp [unix seconds].
 *
 * Note: legacy also raised a "GSM signal lost" alert from `position.gsm_signal`, which the v2
 * payload does not expose — that alert is intentionally omitted here.
 */
export type VehicleStatus = 'drive' | 'idle' | 'rest' | 'alert'

export interface StatusPalette {
  color: string
  tint: string
}

/** Status palette — same hues as legacy mapv3 (meaningful, kept recognizable across themes). */
export const STATUS_PALETTE: Record<VehicleStatus, StatusPalette> = {
  drive: { color: '#16a34a', tint: 'rgba(22,163,74,.14)' },
  idle: { color: '#9aa6b6', tint: 'rgba(154,166,182,.18)' },
  rest: { color: '#0284c7', tint: 'rgba(2,132,199,.14)' },
  alert: { color: '#dc2626', tint: 'rgba(220,38,38,.14)' }
}

// Thresholds identical to legacy mapv3.
const ALERT_POSITION_STALE_SECONDS = 45 * 60
const ALERT_POSITION_STALE_IGNITION_OFF_SECONDS = 120 * 60
const ALERT_NO_POWER_MAX_MV = 1000

/** i18n translate function accepting an optional named-interpolation payload (Vue's $t). */
export type AlertLabeller = (key: string, named?: Record<string, unknown>) => string

function toFiniteNumber(value: unknown): number | null {
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

function positionAgeSeconds(v: any): number {
  const ts = toFiniteNumber(v?.device_timestamp)
  if (ts === null) return Number.POSITIVE_INFINITY
  return Math.floor(Date.now() / 1000) - ts
}

/** Alert list for a vehicle (empty when healthy). Labels come from i18n via `t`. */
export function collectVehicleAlerts(v: any, t: AlertLabeller): string[] {
  const alerts: string[] = []

  const voltage = toFiniteNumber(v?.voltage)
  if (voltage !== null && voltage <= ALERT_NO_POWER_MAX_MV) {
    alerts.push(t('components.map.list.alerts.power'))
  }

  const staleThreshold = v?.ignition
    ? ALERT_POSITION_STALE_SECONDS
    : ALERT_POSITION_STALE_IGNITION_OFF_SECONDS
  if (positionAgeSeconds(v) > staleThreshold) {
    alerts.push(t('components.map.list.alerts.position', { min: Math.round(staleThreshold / 60) }))
  }

  const satellites = toFiniteNumber(v?.satellites)
  if (satellites !== null && satellites <= 0) {
    alerts.push(t('components.map.list.alerts.gps'))
  }

  return alerts
}

/** drive (moving) / idle (ignition on, stopped) / rest (off) / alert (any alert present). */
export function deriveStatus(v: any, alerts: string[]): VehicleStatus {
  if (alerts.length) return 'alert'
  const speed = toFiniteNumber(v?.speed) ?? 0
  if (speed > 0) return 'drive'
  if (v?.ignition) return 'idle'
  return 'rest'
}

/** Formatted speed for the list ("42 km/h" or "—" when stopped/unknown). */
export function speedText(v: any): string {
  const s = toFiniteNumber(v?.speed)
  return s !== null && s > 0 ? Math.round(s) + ' km/h' : '—'
}
